import { PaperClipOutlined } from "@ant-design/icons";

import {
  ASK_FOR_MORE_CREDITS_LABEL,
  openAskForMoreCreditsChat,
  UPGRADE_FROM_TRIAL_MESSAGE,
} from "../../../util/askForMoreCredits";
import type { CopilotMessage, CopilotToolCall } from "./types";
import ActivityBlock from "./ActivityBlock";
import DownloadableDocumentMessage from "./DownloadableDocumentMessage";
import GeneratedDocumentCards from "./GeneratedDocumentCards";
import MessageActions from "./MessageActions";
import MessageCitations from "./MessageCitations";
import RichContentBlocks from "./RichContentBlocks";
import styles from "./styles.module.scss";

const ConversationMessage = ({
  message,
  onSubmitForm,
  isFinal = false,
  streaming = false,
  onRegenerate,
  onRetry,
  citationTools,
}: {
  message: CopilotMessage;
  onSubmitForm?: (text: string) => void | Promise<void>;
  isFinal?: boolean;
  streaming?: boolean;
  onRegenerate?: () => void;
  onRetry?: () => void;
  /** Tools whose sources this reply may cite: every tool in the
   *  conversation so far, since an answer often cites an earlier step. */
  citationTools?: CopilotToolCall[];
}) => {
  if (message.role === "user") {
    return (
      <div className={styles.userRow}>
        <div className={styles.userBubble}>
          {(message.attachments?.length ?? 0) > 0 && (
            <div className={styles.messageAttachments}>
              {message.attachments?.map((attachment) => (
                <span key={`${attachment.id ?? "local"}-${attachment.name}`}>
                  <PaperClipOutlined />
                  {attachment.name}
                </span>
              ))}
            </div>
          )}
          {message.content}
        </div>
      </div>
    );
  }

  // Flag-seeded conversations include both a plain-text fallback and the
  // structured flag card. This client renders the card, so showing the
  // fallback too would repeat the same narrative, conditions and vitals.
  const hasRenderableFlagCard = message.richContent?.some(
    (part) =>
      part.type === "neo_flag_card" &&
      ((typeof part.narrative === "string" &&
        part.narrative.trim().length > 0) ||
        (Array.isArray(part.conditions) && part.conditions.length > 0) ||
        (!!part.vitals && typeof part.vitals === "object"))
  );

  return (
    <div className={styles.assistantRow}>
      {(message.reasoning || (message.tools?.length ?? 0) > 0) && (
        <ActivityBlock
          reasoning={message.reasoning}
          tools={message.tools ?? []}
        />
      )}
      {message.content && !hasRenderableFlagCard && (
        <MessageCitations tools={citationTools ?? message.tools}>
          <div className={styles.assistantContent}>
            <DownloadableDocumentMessage
              source={message.content}
              streaming={isFinal && streaming}
            />
          </div>
        </MessageCitations>
      )}
      {(message.richContent?.length ?? 0) > 0 && (
        <RichContentBlocks
          parts={message.richContent ?? []}
          onSubmitForm={onSubmitForm}
        />
      )}
      <GeneratedDocumentCards tools={message.tools ?? []} />
      {message.truncated && !message.error && (
        <p className={styles.messageNotice}>
          This response was cut off because it reached the length limit.
        </p>
      )}
      {message.stopped && !message.error && (
        <p className={styles.messageNotice}>You stopped this response.</p>
      )}
      {message.error && (
        <div className={styles.messageError} role="alert">
          {message.errorTitle && <strong>{message.errorTitle}</strong>}
          <span>{message.error}</span>
          {(message.errorKind === "billing" ||
            message.errorKind === "trial_ended") && (
            <button
              type="button"
              className={styles.messageErrorAction}
              onClick={() =>
                openAskForMoreCreditsChat(
                  message.errorKind === "trial_ended"
                    ? UPGRADE_FROM_TRIAL_MESSAGE
                    : undefined
                )
              }
            >
              {ASK_FOR_MORE_CREDITS_LABEL}
            </button>
          )}
          {isFinal && !streaming && message.retryable !== false && onRetry && (
            <button
              type="button"
              className={styles.messageErrorAction}
              onClick={onRetry}
            >
              Try again
            </button>
          )}
        </div>
      )}
      <MessageActions
        message={message}
        isFinal={isFinal}
        streaming={streaming}
        onRegenerate={onRegenerate}
      />
    </div>
  );
};

export default ConversationMessage;
